import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import AdminLayout from '@/layouts/AdminLayout';
import AuthContinuePage from '@/pages/AuthContinuePage';
import HandoffErrorPage from '@/pages/HandoffErrorPage';
import MfaSettingsPage from '@/pages/MfaSettingsPage';
import PlatformSecurityCenter from '@/pages/PlatformSecurityCenter';
import Dashboard from '@/pages/SuperAdmin/Dashboard';
import BillingManagement from '@/pages/SuperAdmin/BillingManagement';
import SystemAdministration from '@/pages/SuperAdmin/SystemAdministration';
import TenantManagementV2 from '@/pages/SuperAdmin/TenantManagementV2';
import CreditsWalletPanel from '@/pages/SuperAdmin/components/CreditsWalletPanel';
import PlatformAuditLogPanel from '@/pages/SuperAdmin/components/PlatformAuditLogPanel';
import QueueHealthPanel from '@/pages/SuperAdmin/components/QueueHealthPanel';
import UsageAnalyticsPanel from '@/pages/SuperAdmin/components/UsageAnalyticsPanel';
import { getDefaultPathForTarget, normalizeAppTarget, type AppTarget } from '@/routes/targetRouting';

const OPERATOR_ROLES = new Set(['operator', 'key_account_manager', 'support', 'billing_admin']);
const PLATFORM_ADMIN_ROLES = new Set(['super_admin', 'platform_admin']);
const AUTH_TOKEN_KEYS = ['auth_token', 'auth_v2_accessToken', 'accessToken', 'authToken', 'token'];

const getAppTarget = (): AppTarget => normalizeAppTarget(String(process.env.VITE_APP_TARGET || ''));

const getCurrentHost = (): string => {
  if (typeof window === 'undefined') return 'admin.bassitai.com';
  return String(window.location.host || '').split(':')[0].toLowerCase();
};

const isLocalHost = (host: string): boolean =>
  host === 'localhost' || host.endsWith('.localhost') || host === '127.0.0.1';

const getBaseDomain = (host: string): string => {
  if (!host) return 'bassitai.com';
  if (isLocalHost(host)) return 'localhost';
  const parts = host.split('.').filter(Boolean);
  if (parts.length < 2) return host;
  return parts.slice(-2).join('.');
};

const resolveSubdomainHost = (subdomain: string): string => {
  const currentHost = getCurrentHost();
  const baseDomain = getBaseDomain(currentHost);
  if (baseDomain === 'localhost') return `${subdomain}.localhost`;
  return `${subdomain}.${baseDomain}`;
};

const getPreferredProtocol = (): 'http' | 'https' => {
  const currentHost = getCurrentHost();
  return isLocalHost(currentHost) ? 'http' : 'https';
};

const readAccessToken = (): string => {
  if (typeof window === 'undefined') return '';
  for (const key of AUTH_TOKEN_KEYS) {
    const value = String(window.localStorage.getItem(key) || '').trim();
    if (value && value.split('.').length >= 3) {
      return value;
    }
  }
  return '';
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
};

const isAuthenticated = (): boolean => {
  const token = readAccessToken();
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  const exp = Number(payload?.exp || 0);
  if (exp > 0 && Date.now() >= exp * 1000) return false;
  return true;
};

const getEffectiveRole = (): string => {
  const token = readAccessToken();
  if (!token) return '';
  const payload = decodeJwtPayload(token);
  return String(payload?.systemRole || payload?.role || '').trim().toLowerCase();
};

const buildLoginUrl = (): string => {
  const protocol = getPreferredProtocol();
  const loginHost = resolveSubdomainHost('login');
  const returnTo = getCurrentHost();
  return `${protocol}://${loginHost}/login?returnTo=${encodeURIComponent(returnTo)}`;
};

const buildConsoleUrl = (pathWithQueryAndHash: string): string => {
  const protocol = getPreferredProtocol();
  const consoleHost = resolveSubdomainHost('console');
  return `${protocol}://${consoleHost}${pathWithQueryAndHash}`;
};

const buildAdminUrl = (pathWithQueryAndHash: string): string => {
  const protocol = getPreferredProtocol();
  const adminHost = resolveSubdomainHost('admin');
  return `${protocol}://${adminHost}${pathWithQueryAndHash}`;
};

const buildTenantUrl = (pathWithQueryAndHash: string): string => {
  const protocol = getPreferredProtocol();
  const tenantHost = resolveSubdomainHost('app');
  return `${protocol}://${tenantHost}${pathWithQueryAndHash}`;
};

function SilentExternalRedirect({ to }: { to: string }) {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.location.replace(to);
    }
  }, [to]);
  return null;
}

function FailSafeNoRoutesPage() {
  return (
    <main style={{ fontFamily: 'Inter, sans-serif', padding: '24px' }}>
      <h1>No routes exposed</h1>
      <p>
        `VITE_APP_TARGET` is missing or invalid. Set it to <code>console</code> or <code>admin</code>.
      </p>
    </main>
  );
}

function RequireAuth({
  appTarget,
  children,
}: {
  appTarget: Exclude<AppTarget, 'invalid'>;
  children: React.ReactNode;
}) {
  if (!isAuthenticated()) {
    return <SilentExternalRedirect to={buildLoginUrl()} />;
  }

  const role = getEffectiveRole();
  const pathWithQueryAndHash =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}${window.location.hash}`
      : '/';

  if (appTarget === 'admin' && !PLATFORM_ADMIN_ROLES.has(role)) {
    return <SilentExternalRedirect to={buildConsoleUrl(pathWithQueryAndHash)} />;
  }

  if (appTarget === 'console' && !OPERATOR_ROLES.has(role) && !PLATFORM_ADMIN_ROLES.has(role)) {
    return <SilentExternalRedirect to={buildTenantUrl(pathWithQueryAndHash)} />;
  }

  return <>{children}</>;
}

function LegacySuperAdminRedirect() {
  const location = useLocation();
  const suffix = location.pathname.replace(/^\/super-admin/, '');
  if (!suffix || suffix === '/') {
    return <Navigate to="/dashboard" replace />;
  }
  if (suffix === '/login') {
    return <SilentExternalRedirect to={buildLoginUrl()} />;
  }
  return <Navigate to={suffix} replace />;
}

export default function AppRoutes() {
  const appTarget = getAppTarget();
  const defaultPath = getDefaultPathForTarget(appTarget);

  if (appTarget === 'invalid') {
    return (
      <Routes>
        <Route path="*" element={<FailSafeNoRoutesPage />} />
      </Routes>
    );
  }

  const isAdminTarget = appTarget === 'admin';

  return (
    <Routes>
      <Route path="/" element={<Navigate to={defaultPath} replace />} />
      <Route path="/login" element={<SilentExternalRedirect to={buildLoginUrl()} />} />
      <Route path="/super-admin/login" element={<SilentExternalRedirect to={buildLoginUrl()} />} />
      <Route path="/auth/continue" element={<AuthContinuePage appTarget={appTarget} />} />
      <Route path="/handoff-error" element={<HandoffErrorPage appTarget={appTarget} />} />

      <Route
        element={
          <RequireAuth appTarget={appTarget}>
            <AdminLayout appTarget={appTarget} />
          </RequireAuth>
        }
      >
        {isAdminTarget ? (
          <>
            <Route path="/system" element={<SystemAdministration />} />
            <Route path="/cell-management" element={<SystemAdministration />} />
            <Route path="/workers" element={<QueueHealthPanel />} />
            <Route path="/worker-control" element={<QueueHealthPanel />} />
            <Route path="/settings/mfa" element={<MfaSettingsPage />} />
            <Route path="/audit-log" element={<PlatformAuditLogPanel />} />
            <Route path="/dashboard" element={<Dashboard embedded />} />
            <Route path="/security" element={<PlatformSecurityCenter />} />
          </>
        ) : (
          <>
            <Route path="/dashboard" element={<Dashboard embedded />} />
            <Route path="/tenants" element={<TenantManagementV2 />} />
            <Route path="/billing" element={<BillingManagement />} />
            <Route path="/credits" element={<CreditsWalletPanel />} />
            <Route path="/analytics" element={<UsageAnalyticsPanel />} />
          </>
        )}
      </Route>

      {isAdminTarget ? (
        <>
          <Route path="/tenants" element={<SilentExternalRedirect to={buildConsoleUrl('/tenants')} />} />
          <Route path="/billing" element={<SilentExternalRedirect to={buildConsoleUrl('/billing')} />} />
          <Route path="/credits" element={<SilentExternalRedirect to={buildConsoleUrl('/credits')} />} />
          <Route path="/analytics" element={<SilentExternalRedirect to={buildConsoleUrl('/analytics')} />} />
        </>
      ) : (
        <>
          <Route path="/system" element={<SilentExternalRedirect to={buildAdminUrl('/system')} />} />
          <Route path="/workers" element={<SilentExternalRedirect to={buildAdminUrl('/workers')} />} />
          <Route path="/audit-log" element={<SilentExternalRedirect to={buildAdminUrl('/audit-log')} />} />
          <Route path="/security" element={<SilentExternalRedirect to={buildAdminUrl('/security')} />} />
          <Route path="/settings/mfa" element={<SilentExternalRedirect to={buildAdminUrl('/settings/mfa')} />} />
        </>
      )}

      <Route path="/super-admin/*" element={<LegacySuperAdminRedirect />} />
      <Route path="*" element={<Navigate to={defaultPath} replace />} />
    </Routes>
  );
}
